import { useCallback, useEffect, useState } from "react";
import { Upload, FileArchive, BrainCircuit, CheckCircle2, AlertTriangle, XCircle, Loader2,
  ArrowLeftRight, Wrench, Monitor, Tablet, Smartphone, Save, RefreshCw, Package, Palette } from "lucide-react";
import { toast } from "sonner";
import api, { errMsg, API } from "../../lib/api";
import { PageHeader } from "../../components/Shared";
import ThemeHtmlRenderer from "../../components/ThemeHtmlRenderer";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";

const STEPS = ["Upload", "AI Analyze", "Field Mapping", "Validate", "Preview", "Save"];
const STEP_OF = { scanned: 1, analyzing: 1, analyzed: 2, converting: 3, converted: 3, validated: 3, saved: 5, failed: 0 };
const FIELD_TARGETS = [
  ["business_name", "Business Name"], ["tagline", "Tagline"], ["logo", "Logo"], ["favicon", "Favicon"],
  ["phone", "Business Phone"], ["email", "Business Email"], ["address", "Business Address"],
  ["whatsapp", "Business WhatsApp Number"], ["social_links", "Social Media Links"],
  ["about", "About Content"], ["hours", "Business Hours"], ["cta_primary", "Primary CTA Text"],
];
const Card = ({ children, className = "", testid }) => (
  <div data-testid={testid} className={`bg-card border border-border rounded-2xl p-6 ${className}`}>{children}</div>
);
const CheckIcon = ({ s }) => s === "pass" ? <CheckCircle2 className="h-4 w-4 text-emerald-500 shrink-0" />
  : s === "warn" ? <AlertTriangle className="h-4 w-4 text-amber-500 shrink-0" />
  : <XCircle className="h-4 w-4 text-red-500 shrink-0" />;

export default function RootImport() {
  const [imports, setImports] = useState([]);
  const [imp, setImp] = useState(null);
  const [busy, setBusy] = useState("");
  const [mappings, setMappings] = useState([]);
  const [previewHtml, setPreviewHtml] = useState("");
  const [device, setDevice] = useState("desktop");
  const [saveForm, setSaveForm] = useState({ name: "", category: "", description: "", preview: "", status: "active", is_ecommerce: true });

  const loadList = useCallback(() => {
    api.get("/root/import").then((r) => setImports(r.data.imports)).catch(() => {});
  }, []);
  useEffect(() => { loadList(); }, [loadList]);

  const open = (iid) => api.get(`/root/import/${iid}`).then((r) => {
    setImp(r.data); setMappings(r.data.mappings || []); setPreviewHtml("");
    const a = r.data.analysis || {};
    setSaveForm((f) => ({ ...f, name: a.suggested_name || "", category: a.suggested_category || "",
      description: a.description || "", is_ecommerce: !!a.ecommerce?.is_ecommerce }));
  }).catch((e) => toast.error(errMsg(e)));

  const upload = async (file) => {
    if (!file) return;
    setBusy("upload");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/root/import/upload", fd);
      toast.success(`Uploaded — ${r.data.scan.file_count} files extracted`);
      setImp(r.data); setMappings([]); setPreviewHtml(""); loadList();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(""); }
  };

  const act = async (label, fn) => {
    setBusy(label);
    try {
      const r = await fn();
      if (r?.data) { setImp(r.data); if (r.data.mappings) setMappings(r.data.mappings); }
      return r;
    } catch (e) { toast.error(errMsg(e)); return null; } finally { setBusy(""); }
  };

  const analyze = () => act("analyze", () => api.post(`/root/import/${imp.id}/analyze`));
  const convert = () => act("convert", async () => {
    await api.put(`/root/import/${imp.id}/mappings`, { mappings });
    return api.post(`/root/import/${imp.id}/convert`);
  });
  const validate = () => act("validate", () => api.post(`/root/import/${imp.id}/validate`));
  const autofix = async () => {
    const r = await act("autofix", () => api.post(`/root/import/${imp.id}/autofix`));
    if (r?.data?.fixes?.length) toast.success(`Auto-fixed: ${r.data.fixes[r.data.fixes.length - 1] || "issues"}`);
  };
  const loadPreview = () => act("preview", async () => {
    const r = await api.get(`/root/import/${imp.id}/preview`, { responseType: "text" });
    const token = localStorage.getItem("zylo_token") || "";
    setPreviewHtml(r.data.replaceAll("__TK__", token));
    return null;
  });
  // Framework (React/Next/TanStack) builds hydrate the WHOLE document — they cannot run
  // inside another React page. Preview = dedicated full-page window (no iframe).
  const isSpa = !!imp?.package && imp.package.engine === "spa";
  const openFullPreview = (d = "desktop") => {
    const token = localStorage.getItem("zylo_token") || "";
    const sizes = { desktop: "width=1440,height=900", tablet: "width=768,height=1024", mobile: "width=390,height=844" };
    window.open(`${API}/root/import/${imp.id}/preview-page?token=${encodeURIComponent(token)}`, "_blank", sizes[d]);
  };
  const save = async () => {
    const r = await act("save", () => api.post(`/root/import/${imp.id}/save`, saveForm));
    if (r?.data?.ok) { toast.success(`Theme "${r.data.theme.name}" saved to the library`); loadList(); }
  };

  const step = imp ? Math.max(STEP_OF[imp.status] ?? 0, previewHtml ? 4 : 0) : 0;

  // poll while a React/Vite build runs server-side (npm install + build, minutes)
  useEffect(() => {
    if (imp?.status !== "building") return undefined;
    const t = setInterval(() => open(imp.id), 3000);
    return () => clearInterval(t);
  }, [imp?.status, imp?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const checks = imp?.validation || [];
  const critical = checks.filter((c) => c.status === "critical").length;
  const a = imp?.analysis || {};
  const widths = { desktop: "100%", tablet: "768px", mobile: "390px" };

  return (
    <div data-testid="root-import-page" className="max-w-5xl">
      <PageHeader title="Import Website / Theme" subtitle="Upload a project (Lovable, Emergent, AI Studio, HTML/React) and convert it into a reusable MILTROO theme." testid="import-title" />

      {/* Stepper */}
      <div className="flex flex-wrap gap-2 mb-6" data-testid="import-stepper">
        {STEPS.map((s, i) => (
          <span key={s} data-testid={`step-${i}`} className={`px-3 py-1.5 rounded-full text-xs font-extrabold ${i <= step ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"}`}>
            {i + 1}. {s}
          </span>
        ))}
      </div>

      {/* Previous imports */}
      {imports.length > 0 && (
        <Card testid="import-history" className="mb-6">
          <p className="text-xs uppercase tracking-widest font-extrabold text-muted-foreground mb-3">Previous Imports</p>
          <div className="flex flex-wrap gap-2">
            {imports.map((x) => (
              <button key={x.id} data-testid={`import-item-${x.id}`} onClick={() => open(x.id)}
                className={`border rounded-xl px-3 py-2 text-xs font-bold flex items-center gap-2 ${imp?.id === x.id ? "border-primary" : "border-border hover:border-primary/40"}`}>
                <FileArchive className="h-3.5 w-3.5 text-primary" /> {x.filename}
                <span className="text-muted-foreground font-semibold">· {x.status}{x.theme_id ? " · saved" : ""}</span>
              </button>
            ))}
          </div>
        </Card>
      )}

      {/* 1. Upload */}
      <Card testid="upload-card" className="mb-6">
        <p className="font-display font-bold text-lg mb-1 flex items-center gap-2"><Upload className="h-5 w-5 text-primary" /> Upload Project (ZIP)</p>
        <p className="text-sm text-muted-foreground mb-4">Static HTML, React source, or full project export. Max 60 MB. Files are isolated and never executed on the server.</p>
        <Input data-testid="zip-input" type="file" accept=".zip" disabled={!!busy}
          onChange={(e) => upload(e.target.files?.[0])} className="h-12" />
        {busy === "upload" && <p className="text-sm mt-2 flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Uploading & scanning…</p>}
        {imp && (
          <div className="mt-4 text-sm grid sm:grid-cols-3 gap-3" data-testid="scan-summary">
            <p><span className="font-extrabold">{imp.scan.file_count}</span> files</p>
            <p>Framework hint: <span className="font-extrabold">{imp.scan.framework_hint}</span></p>
            <p>Entry: <span className="font-mono font-bold">{imp.scan.entry_html || "—"}</span></p>
          </div>
        )}
      </Card>

      {imp && (
        <>
          {/* 2. Analyze */}
          <Card testid="analyze-card" className="mb-6">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <p className="font-display font-bold text-lg flex items-center gap-2"><BrainCircuit className="h-5 w-5 text-primary" /> AI Project Analysis</p>
              <Button data-testid="analyze-button" onClick={analyze} disabled={!!busy} className="rounded-full font-bold">
                {busy === "analyze" ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                {imp.analysis ? "Re-Analyze" : "Analyze Project"}
              </Button>
            </div>
            {imp.analysis && (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mt-4 text-sm" data-testid="analysis-summary">
                {[
                  ["Framework", a.framework], ["Source Tool", a.source_tool], ["Pages", (a.pages || []).length],
                  ["Components", Array.isArray(a.components) ? a.components.length : (a.components || "—")],
                  ["Responsive", typeof a.responsive === "object" && a.responsive ? Object.entries(a.responsive).map(([k, v]) => `${k}: ${v}`).join(", ") : (a.responsive || "—")],
                  ["E-commerce", a.ecommerce?.is_ecommerce ? "Yes" : "No"],
                  ["Navigation", a.navigation ? "Detected" : "—"],
                  ["Forms", Array.isArray(a.forms) ? a.forms.length : (a.forms || "—")],
                  ["Animations", Array.isArray(a.animations) ? a.animations.length : (a.animations ? "Detected" : "—")],
                  ["AI Engine", a.ai ? "GPT-5.4" : "Heuristic fallback"],
                ].map(([k, v]) => (
                  <div key={k} className="border border-border rounded-xl px-3 py-2.5">
                    <p className="text-[10px] uppercase tracking-widest font-extrabold text-muted-foreground">{k}</p>
                    <p className="font-bold mt-0.5 truncate">{String(v)}</p>
                  </div>
                ))}
              </div>
            )}
          </Card>

          {/* 3. Field mapping */}
          {imp.analysis && (
            <Card testid="mapping-card" className="mb-6">
              <p className="font-display font-bold text-lg flex items-center gap-2 mb-1"><ArrowLeftRight className="h-5 w-5 text-primary" /> Dynamic Field Mapping</p>
              <p className="text-sm text-muted-foreground mb-4">Detected hard-coded values → MILTROO dynamic fields. Owners edit these later from their dashboard.</p>
              {mappings.length === 0 && <p className="text-sm text-muted-foreground">No hard-coded business fields detected.</p>}
              <div className="space-y-2">
                {mappings.map((m, i) => (
                  <div key={i} data-testid={`mapping-row-${i}`} className="grid sm:grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)_auto] items-center gap-2 border border-border rounded-xl p-3">
                    <div className="min-w-0">
                      <p className="text-xs font-bold">{m.label}</p>
                      <p className="text-xs text-muted-foreground font-mono truncate">"{m.detected_value}"</p>
                    </div>
                    <span className="text-muted-foreground text-xs hidden sm:block">→</span>
                    <select data-testid={`mapping-target-${i}`} value={m.map_to}
                      onChange={(e) => setMappings((ms) => ms.map((x, j) => j === i ? { ...x, map_to: e.target.value } : x))}
                      className="h-9 rounded-lg border border-border bg-background px-2 text-xs font-bold">
                      <option value="">(skip — keep as-is)</option>
                      {FIELD_TARGETS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                    </select>
                    <input type="checkbox" data-testid={`mapping-include-${i}`} checked={!!m.include}
                      onChange={(e) => setMappings((ms) => ms.map((x, j) => j === i ? { ...x, include: e.target.checked } : x))}
                      className="h-4 w-4 justify-self-end" />
                  </div>
                ))}
              </div>
              <Button data-testid="convert-button" onClick={convert} disabled={!!busy || (!imp.scan.entry_html && !imp.scan.needs_build) || imp.status === "building"} className="rounded-full font-bold mt-4">
                {busy === "convert" || imp.status === "building" ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Package className="h-4 w-4 mr-2" />}
                Convert to MILTROO Theme
              </Button>
              {imp.status === "building" && (
                <p className="text-sm mt-3 flex items-center gap-2" data-testid="build-status">
                  <Loader2 className="h-4 w-4 animate-spin" /> Building React/Vite project (npm install + build)… this can take a few minutes. The page updates automatically.
                </p>
              )}
              {imp.status === "failed" && (
                <pre className="text-xs mt-3 text-red-500 font-mono whitespace-pre-wrap bg-red-500/10 rounded-lg p-3" data-testid="build-error">{imp.error}</pre>
              )}
            </Card>
          )}

          {/* 4. Validation */}
          {imp.package && (
            <Card testid="validation-card" className="mb-6">
              <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
                <p className="font-display font-bold text-lg">Validation {critical > 0 && <span className="text-red-500 text-sm font-bold">({critical} critical)</span>}</p>
                <div className="flex gap-2">
                  <Button data-testid="autofix-button" variant="outline" onClick={autofix} disabled={!!busy} className="rounded-full font-bold">
                    {busy === "autofix" ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Wrench className="h-4 w-4 mr-2" />} Auto-Fix
                  </Button>
                  <Button data-testid="validate-button" variant="outline" onClick={validate} disabled={!!busy} className="rounded-full font-bold">
                    {busy === "validate" ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <RefreshCw className="h-4 w-4 mr-2" />} Re-Validate
                  </Button>
                </div>
              </div>
              <div className="space-y-2" data-testid="validation-list">
                {checks.map((c) => (
                  <div key={c.id} data-testid={`check-${c.id}`} className="flex items-start gap-2.5 text-sm">
                    <CheckIcon s={c.status} />
                    <div><span className="font-bold">{c.label}</span>
                      {c.detail && <span className="text-muted-foreground"> — {c.detail}</span>}</div>
                  </div>
                ))}
              </div>
              {imp.fixes?.length > 0 && <p className="text-xs text-muted-foreground mt-3">Auto-fixes applied: {imp.fixes.join(", ")}</p>}
              <Button data-testid="preview-button" onClick={isSpa ? () => openFullPreview("desktop") : loadPreview} disabled={!!busy} variant="outline" className="rounded-full font-bold mt-4">
                {busy === "preview" ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Monitor className="h-4 w-4 mr-2" />} {isSpa ? "Open Interactive Preview" : "Load Preview"}
              </Button>
            </Card>
          )}

          {/* 5. Preview */}
          {isSpa && (
            <Card testid="preview-card" className="mb-6">
              <p className="font-display font-bold text-lg mb-2">Interactive Preview (full page)</p>
              <p className="text-sm text-muted-foreground mb-4">
                This is a React/framework project — its JavaScript hydrates the entire page, so the interactive
                preview opens in a dedicated window (not an iframe). All original behaviors (menus, sliders, tabs,
                buttons) run natively; MILTROO-mapped actions (Shop Now, products, cart) route to the live demo store.
              </p>
              <div className="flex gap-2" data-testid="device-toggles">
                {[["desktop", Monitor], ["tablet", Tablet], ["mobile", Smartphone]].map(([d, I]) => (
                  <Button key={d} data-testid={`device-${d}`} size="sm" variant="outline" onClick={() => openFullPreview(d)} className="rounded-full font-bold">
                    <I className="h-4 w-4 mr-1.5" /> {d}
                  </Button>
                ))}
              </div>
            </Card>
          )}
          {previewHtml && !isSpa && (
            <Card testid="preview-card" className="mb-6">
              <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
                <p className="font-display font-bold text-lg">Preview (demo data)</p>
                <div className="flex gap-1.5" data-testid="device-toggles">
                  {[["desktop", Monitor], ["tablet", Tablet], ["mobile", Smartphone]].map(([d, I]) => (
                    <Button key={d} data-testid={`device-${d}`} size="sm" variant={device === d ? "default" : "outline"}
                      onClick={() => setDevice(d)} className="rounded-full"><I className="h-4 w-4" /></Button>
                  ))}
                </div>
              </div>
              <div className="border border-border rounded-xl bg-muted/30 overflow-hidden">
                <div className="overflow-auto max-h-[75vh] flex justify-center py-4 px-2">
                  <div style={{ width: widths[device], maxWidth: "100%" }} className="bg-white transition-all shadow-lg">
                    <ThemeHtmlRenderer html={previewHtml} mode="preview" testid="theme-preview-frame" />
                  </div>
                </div>
              </div>
            </Card>
          )}

          {/* 6. Save */}
          {imp.package && (
            <Card testid="save-card" className="mb-6">
              <p className="font-display font-bold text-lg flex items-center gap-2 mb-4"><Palette className="h-5 w-5 text-primary" /> Save as MILTROO Theme</p>
              {imp.theme_id ? (
                <p className="text-sm font-bold text-emerald-600 flex items-center gap-2" data-testid="saved-note">
                  <CheckCircle2 className="h-4 w-4" /> Saved to the theme library — available when creating websites.
                </p>
              ) : (
                <div className="grid sm:grid-cols-2 gap-3">
                  <Input data-testid="save-name" placeholder="Theme Name *" value={saveForm.name}
                    onChange={(e) => setSaveForm({ ...saveForm, name: e.target.value })} />
                  <Input data-testid="save-category" placeholder="Category (e.g. Fashion)" value={saveForm.category}
                    onChange={(e) => setSaveForm({ ...saveForm, category: e.target.value })} />
                  <Input data-testid="save-preview" placeholder="Preview image URL (optional)" value={saveForm.preview}
                    onChange={(e) => setSaveForm({ ...saveForm, preview: e.target.value })} className="sm:col-span-2" />
                  <Input data-testid="save-description" placeholder="Description" value={saveForm.description}
                    onChange={(e) => setSaveForm({ ...saveForm, description: e.target.value })} className="sm:col-span-2" />
                  <label className="flex items-center gap-2 text-sm font-bold">
                    <input type="checkbox" data-testid="save-ecommerce" checked={saveForm.is_ecommerce}
                      onChange={(e) => setSaveForm({ ...saveForm, is_ecommerce: e.target.checked })} className="h-4 w-4" />
                    E-commerce theme (products / cart / checkout)
                  </label>
                  <select data-testid="save-status" value={saveForm.status}
                    onChange={(e) => setSaveForm({ ...saveForm, status: e.target.value })}
                    className="h-10 rounded-lg border border-border bg-background px-3 text-sm font-bold">
                    <option value="active">Active (visible in library)</option>
                    <option value="draft">Draft (hidden)</option>
                  </select>
                  <div className="sm:col-span-2">
                    <Button data-testid="save-theme-button" onClick={save} disabled={!!busy || critical > 0 || !saveForm.name.trim()}
                      className="rounded-full font-bold h-11 px-8">
                      {busy === "save" ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
                      {critical > 0 ? "Fix Critical Errors to Publish" : "Save as MILTROO Theme"}
                    </Button>
                  </div>
                </div>
              )}
            </Card>
          )}
        </>
      )}
    </div>
  );
}
